import React from 'react';

interface HeaderSectionProps {
  title: string;
  subtitle: string;
  backgroundImage: string;
  ariaLabel?: string;
  height?: string;
  titleSize?: 'small' | 'medium' | 'large';
  subtitleSize?: 'small' | 'medium' | 'large';
  overlayOpacity?: 'light' | 'medium' | 'dark';
  textAlignment?: 'left' | 'center';
  maxWidth?: string;
}

/**
 * Shared HeaderSection Component
 * Displays a hero header section with background image and text overlay
 * Used across multiple pages for consistent design
 */
const HeaderSection: React.FC<HeaderSectionProps> = ({ 
  title,
  subtitle,
  backgroundImage,
  ariaLabel,
  height = 'h-[400px]',
  titleSize = 'large',
  subtitleSize = 'large',
  overlayOpacity = 'medium',
  textAlignment = 'left',
  maxWidth = 'max-w-2xl'
}) => {
  // Title size classes
  const titleClasses = {
    small: 'text-3xl lg:text-4xl',
    medium: 'text-4xl lg:text-5xl',
    large: 'text-5xl lg:text-6xl',
  };

  // Subtitle size classes
  const subtitleClasses = {
    small: 'text-base lg:text-lg',
    medium: 'text-lg lg:text-xl',
    large: 'text-xl lg:text-2xl',
  };

  // Overlay opacity classes
  const overlayClasses = {
    light: 'bg-gradient-to-r from-black/30 to-black/20',
    medium: 'bg-gradient-to-r from-black/50 to-black/30',
    dark: 'bg-gradient-to-r from-black/70 to-black/50',
  };

  // Text alignment classes
  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center mx-auto',
  };

  return (
    <section
      aria-label={ariaLabel || `${title} page header`}
      className={`relative ${height} bg-cover bg-center bg-no-repeat`}
      style={{ backgroundImage: `url('${backgroundImage}')` }}
    >
      {/* Dark gradient overlay for better text readability */}
      <div className={`absolute inset-0 ${overlayClasses[overlayOpacity]}`} />

      {/* Content container */}
      <div className="relative h-full flex items-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className={`${maxWidth} ${alignmentClasses[textAlignment]}`}>
            <h1 className={`text-white font-bold tracking-tight ${titleClasses[titleSize]}`}>
              {title}
            </h1>
            <p className={`mt-4 text-white/90 font-light ${subtitleClasses[subtitleSize]}`}>
              {subtitle}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeaderSection;
